import { useId, useRef } from "react";
import type { ComponentPropsWithRef, ReactNode } from "react";

import { cx } from "./cx";
import { useT } from "../../context/locale";

/*
 * Form controls for the whole console.
 *
 * Every field in the backoffice comes from this file, so a text box in a filter
 * bar, a dialog and a settings page are the same control with the same height,
 * the same focus treatment, the same error styling and the same disabled state.
 * Pages describe *what* they are asking for — a money amount, a date, a choice —
 * and never restate how it should look.
 *
 * Two rules that matter more than the styling:
 *
 * 1. Every component here is declared at module scope. A component declared
 *    inside a render function is a new type on every render, so React unmounts
 *    and remounts it, and anything focused inside loses focus. That is the same
 *    class of bug as the Modal focus effect these were built alongside.
 *
 * 2. Controls are uncontrolled about their own identity: they never key
 *    themselves off changing values, so typing never rebuilds the DOM node.
 */

/* ── Shared appearance ────────────────────────────────────────────────────── */

const CONTROL =
  "w-full rounded-lg border text-[13px] outline-none transition-colors placeholder:opacity-45 " +
  "disabled:cursor-not-allowed disabled:opacity-55";

/** One border colour rule for every control, so an error looks the same anywhere. */
function controlStyle(invalid?: boolean) {
  return {
    background: "var(--surface-inset)",
    borderColor: invalid ? "var(--danger)" : "var(--border)",
    color: "var(--text)",
  };
}

const FOCUS = "focus:border-[var(--accent)]";

/* ── Field wrapper ────────────────────────────────────────────────────────── */

export interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
  /** Set when the control renders its own label (toggles, checkboxes). */
  bare?: boolean;
}

/**
 * Label, required marker, and the one line underneath that is either a hint or
 * an error — never both, because an error replacing the hint is how you notice
 * it. Wrapping in `<label>` means the whole block is a click target for the
 * control inside, which is why controls here do not need their own `id`.
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  return (
    <label className="block">
      <span className="eyebrow mb-1.5 block">
        {label}
        {required ? <span style={{ color: "var(--danger)" }}> *</span> : null}
      </span>
      {children}
      <FieldNote hint={hint} error={error} />
    </label>
  );
}

function FieldNote({
  hint,
  error,
}: {
  hint?: ReactNode;
  error?: string | null;
}) {
  if (error) {
    return (
      <span
        className="mt-1 block text-[11px]"
        style={{ color: "var(--danger)" }}
        role="alert"
      >
        {error}
      </span>
    );
  }
  if (hint)
    return <span className="text-dim mt-1 block text-[11px]">{hint}</span>;
  return null;
}

/** Two even columns on anything wider than a phone — the console's default form shape. */
export function FieldGrid({
  children,
  columns = 2,
}: {
  children: ReactNode;
  columns?: 1 | 2 | 3;
}) {
  return (
    <div
      className={cx(
        "grid gap-4",
        columns === 2 && "sm:grid-cols-2",
        columns === 3 && "sm:grid-cols-2 lg:grid-cols-3",
      )}
    >
      {children}
    </div>
  );
}

/* ── Raw controls ─────────────────────────────────────────────────────────── */

export function Input({
  className,
  invalid,
  ...rest
}: ComponentPropsWithRef<"input"> & { invalid?: boolean }) {
  return (
    <input
      className={cx(CONTROL, FOCUS, "h-10 px-3", className)}
      style={controlStyle(invalid)}
      {...rest}
    />
  );
}

export function Select({
  className,
  invalid,
  children,
  ...rest
}: ComponentPropsWithRef<"select"> & { invalid?: boolean }) {
  return (
    <select
      className={cx(
        CONTROL,
        FOCUS,
        "h-10 cursor-pointer appearance-none bg-no-repeat px-3 pr-8",
        className,
      )}
      style={controlStyle(invalid)}
      {...rest}
    >
      {children}
    </select>
  );
}

export function Textarea({
  className,
  invalid,
  ...rest
}: ComponentPropsWithRef<"textarea"> & { invalid?: boolean }) {
  return (
    <textarea
      className={cx(
        CONTROL,
        FOCUS,
        "min-h-20 resize-y px-3 py-2 leading-relaxed",
        className,
      )}
      style={controlStyle(invalid)}
      {...rest}
    />
  );
}

/* ── Composed fields ──────────────────────────────────────────────────────── */

type Common = {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  disabled?: boolean;
};

type ValueProps<T> = { value: T; onChange: (value: T) => void };

/**
 * Plain text.
 *
 * `onChange` hands over the value rather than the event: every call site in this
 * repo only ever wanted `event.target.value`, and the unwrapping was the one
 * piece of boilerplate repeated in all seventy of them.
 */
export function TextField({
  label,
  hint,
  error,
  required,
  disabled,
  value,
  onChange,
  placeholder,
  autoFocus,
  type = "text",
  autoComplete,
  maxLength,
  uppercase,
  mono,
}: Common &
  ValueProps<string> & {
    placeholder?: string;
    autoFocus?: boolean;
    type?: "text" | "password" | "email" | "tel";
    autoComplete?: string;
    maxLength?: number;
    /** Upper-cases as you type — for codes that are stored upper-case. */
    uppercase?: boolean;
    /** Tabular figures, for codes and identifiers read character by character. */
    mono?: boolean;
  }) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      <Input
        type={type}
        value={value}
        onChange={(event) =>
          onChange(
            uppercase ? event.target.value.toUpperCase() : event.target.value,
          )
        }
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        maxLength={maxLength}
        disabled={disabled}
        invalid={Boolean(error)}
        className={cx(mono && "money", uppercase && "uppercase")}
      />
    </Field>
  );
}

/**
 * Whole numbers only.
 *
 * Filters keystrokes to digits rather than relying on `type="number"`, whose
 * spinners, scroll-wheel capture and locale-dependent parsing all cause more
 * trouble than they solve on a form like this.
 */
export function NumberField({
  label,
  hint,
  error,
  required,
  disabled,
  value,
  onChange,
  placeholder,
  autoFocus,
  maxLength = 12,
  align = "left",
}: Common &
  ValueProps<string> & {
    placeholder?: string;
    autoFocus?: boolean;
    maxLength?: number;
    align?: "left" | "right";
  }) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      <Input
        value={value}
        onChange={(event) =>
          onChange(
            event.target.value.replace(/[^0-9]/g, "").slice(0, maxLength),
          )
        }
        inputMode="numeric"
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        invalid={Boolean(error)}
        className={cx("money", align === "right" && "text-right")}
      />
    </Field>
  );
}

/**
 * An amount of money, as typed — "1234.50".
 *
 * Kept as a string all the way to the caller's `parseMoney`, because turning it
 * into a number here would eat a trailing decimal point mid-typing and fight the
 * person entering it.
 */
export function MoneyField({
  label,
  hint,
  error,
  required,
  disabled,
  value,
  onChange,
  placeholder = "0.00",
  autoFocus,
}: Common &
  ValueProps<string> & { placeholder?: string; autoFocus?: boolean }) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      <Input
        value={value}
        onChange={(event) =>
          onChange(event.target.value.replace(/[^0-9.]/g, "").slice(0, 14))
        }
        inputMode="decimal"
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        invalid={Boolean(error)}
        className="money text-[15px]"
      />
    </Field>
  );
}

export function TextAreaField({
  label,
  hint,
  error,
  required,
  disabled,
  value,
  onChange,
  placeholder,
  rows,
}: Common & ValueProps<string> & { placeholder?: string; rows?: number }) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      <Textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        invalid={Boolean(error)}
      />
    </Field>
  );
}

export interface Option<T extends string = string> {
  value: T;
  label: string;
}

export function SelectField<T extends string = string>({
  label,
  hint,
  error,
  required,
  disabled,
  value,
  onChange,
  options,
}: Common & ValueProps<T> & { options: Option<T>[] }) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      <Select
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
        disabled={disabled}
        invalid={Boolean(error)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/**
 * A date, as `YYYY-MM-DD`.
 *
 * Built on the native date input deliberately. It brings a keyboard-accessible
 * picker, the viewer's own locale and date order, and mobile's native wheel —
 * none of which a hand-rolled calendar gets for free, and all of which it can
 * get wrong. What this adds is the console's own styling, a calendar button that
 * opens the picker from anywhere in the field, and a clear affordance, since the
 * browser's own indicator is nearly invisible against a dark surface.
 */
export function DateField({
  label,
  hint,
  error,
  required,
  disabled,
  value,
  onChange,
  min,
  max,
}: Common & ValueProps<string> & { min?: string; max?: string }) {
  const ref = useRef<HTMLInputElement>(null);

  return (
    <Field label={label} hint={hint} error={error} required={required}>
      <div className="relative">
        <Input
          ref={ref}
          type="date"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          min={min}
          max={max}
          disabled={disabled}
          invalid={Boolean(error)}
          className="pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0"
        />
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          disabled={disabled}
          onClick={() => {
            const el = ref.current;
            if (!el) return;
            el.focus();
            // Not in every browser, and it throws if the input is not visible.
            try {
              el.showPicker?.();
            } catch {
              /* focus alone is a working fallback */
            }
          }}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-[13px] disabled:opacity-40"
          style={{ color: "var(--text-dim)" }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M3 10h18M8 3v4M16 3v4" />
          </svg>
        </button>
      </div>
    </Field>
  );
}

/**
 * A date *and* a clock, as `YYYY-MM-DDTHH:mm` in the viewer's own time zone.
 *
 * Separate from `DateField` rather than a flag on it. The two answer different
 * questions — a filter asks for a day, a correction asks for a moment — and the
 * value formats differ, so one control taking both would put the burden of
 * knowing which shape it is in on every caller.
 *
 * Seconds are deliberately not offered. Rounds are minutes apart and nobody
 * correcting a mis-keyed timestamp knows the second it happened; asking for one
 * would only mean typing a zero.
 */
export function DateTimeField({
  label,
  hint,
  error,
  required,
  disabled,
  value,
  onChange,
  min,
  max,
}: Common & ValueProps<string> & { min?: string; max?: string }) {
  const ref = useRef<HTMLInputElement>(null);

  return (
    <Field label={label} hint={hint} error={error} required={required}>
      <div className="relative">
        <Input
          ref={ref}
          type="datetime-local"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          min={min}
          max={max}
          disabled={disabled}
          invalid={Boolean(error)}
          className="pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0"
        />
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          disabled={disabled}
          onClick={() => {
            const el = ref.current;
            if (!el) return;
            el.focus();
            // Not in every browser, and it throws if the input is not visible.
            try {
              el.showPicker?.();
            } catch {
              /* focus alone is a working fallback */
            }
          }}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-[13px] disabled:opacity-40"
          style={{ color: "var(--text-dim)" }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
        </button>
      </div>
    </Field>
  );
}

/* ── Boolean controls ─────────────────────────────────────────────────────── */

/**
 * A switch, for a setting that takes effect as a state rather than as a
 * submitted value — "rounds are running", "the account is enabled".
 */
export function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
  tone = "accent",
}: {
  label: string;
  hint?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  tone?: "accent" | "success" | "danger";
}) {
  const toneVar =
    tone === "success"
      ? "var(--success)"
      : tone === "danger"
        ? "var(--danger)"
        : "var(--accent)";

  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className="relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        style={{ background: checked ? toneVar : "var(--border-strong)" }}
      >
        <span
          className="absolute top-0.5 size-4 rounded-full transition-[left]"
          style={{
            left: checked ? "1.125rem" : "0.125rem",
            background: "var(--surface)",
          }}
        />
      </button>
      <span className="text-[13px] leading-tight">
        <span className="font-semibold">{label}</span>
        {hint ? (
          <span className="text-dim mt-0.5 block text-[12px] font-normal">
            {hint}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * A checkbox, for acknowledging something before a form is submitted — a
 * different job from a toggle, which changes a setting the moment it is clicked.
 */
export function Checkbox({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  hint?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cx(
        "flex items-start gap-2.5",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-4 shrink-0 accent-[var(--color-red)]"
      />
      <span className="text-muted text-[12px] leading-snug">
        {label}
        {hint ? <span className="text-dim mt-0.5 block">{hint}</span> : null}
      </span>
    </label>
  );
}

/**
 * A small set of mutually exclusive choices, shown all at once.
 *
 * For two to four options where seeing the alternatives is part of the decision
 * — the credit dialog's top up / set / withdraw. More than that, or where the
 * options are just data, belongs in a `SelectField`.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Option<T>[];
  ariaLabel: string;
}) {
  return (
    <div
      className="flex gap-1 rounded-lg p-1"
      style={{ background: "var(--surface-inset)" }}
      role="tablist"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className="flex-1 rounded-md py-1.5 text-[12px] font-semibold transition-colors"
            style={{
              background: active ? "var(--surface)" : "transparent",
              color: active ? "var(--accent)" : "var(--text-dim)",
              boxShadow: active ? "0 1px 2px rgb(0 0 0 / 0.16)" : undefined,
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Which days of the week something happens on.
 *
 * Seven toggles in a row rather than a multi-select: the answer is almost always
 * "these two", and a row of days is a shape an operator reads at a glance and
 * corrects with one click. The same seven items behind a dropdown hide both the
 * choice and the current state.
 *
 * Values are `Date.prototype.getDay` numbers — Sunday 0 — so what the control
 * holds is what the API takes, with nothing translating in between. The week is
 * *rendered* Monday-first because that is how a betting week reads; `ORDER` is
 * presentation and nothing more.
 */
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export function WeekdayField({
  label,
  hint,
  error,
  required,
  disabled,
  value,
  onChange,
  labels,
}: Common &
  ValueProps<number[]> & {
    /** Seven short names, indexed by day number — Sunday first. */
    labels: string[];
  }) {
  const toggle = (day: number) =>
    onChange(
      value.includes(day)
        ? value.filter((entry) => entry !== day)
        : [...value, day].sort((a, b) => a - b),
    );

  return (
    /* Not wrapped in `Field`: that renders a <label>, and a label around seven
       buttons points at no control while swallowing the click it promises. */
    <div className="block">
      <span className="eyebrow mb-1.5 block">
        {label}
        {required ? <span style={{ color: "var(--danger)" }}> *</span> : null}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {WEEKDAY_ORDER.map((day) => {
          const active = value.includes(day);
          return (
            <button
              key={day}
              type="button"
              disabled={disabled}
              aria-pressed={active}
              onClick={() => toggle(day)}
              className={cx(
                "h-8 min-w-[2.6rem] flex-1 rounded-lg border text-[12px] font-semibold transition-colors",
                disabled ? "cursor-not-allowed opacity-50" : "hover:border-[var(--accent)]",
              )}
              style={{
                background: active ? "var(--accent)" : "var(--surface-inset)",
                borderColor: active ? "var(--accent)" : "var(--border)",
                color: active ? "var(--bg-deep)" : "var(--text-dim)",
              }}
            >
              {labels[day]}
            </button>
          );
        })}
      </div>
      <FieldNote hint={hint} error={error} />
    </div>
  );
}

/**
 * A from/to pair on one line — "between 2 and 4", "from 12:00 to 20:00".
 *
 * The history profile is mostly made of these, and two separate fields for one
 * idea doubles the labels an operator has to read while hiding that the pair is
 * a single decision. Equal ends are a legitimate answer and mean "exactly this",
 * which is how a precise count gets asked for.
 */
export function RangeField({
  label,
  hint,
  error,
  required,
  disabled,
  value,
  onChange,
  suffix,
  step = 1,
  min,
  max,
}: Common &
  ValueProps<{ min: number; max: number }> & {
    /** Shown after each box — "%", "per day", ":00". */
    suffix?: string;
    step?: number;
    min?: number;
    max?: number;
  }) {
  const box = (which: "min" | "max") => (
    <div className="flex flex-1 items-center gap-1.5">
      <Input
        type="number"
        value={String(value[which])}
        step={step}
        min={min}
        max={max}
        disabled={disabled}
        invalid={Boolean(error)}
        onChange={(event) => onChange({ ...value, [which]: Number(event.target.value) })}
        className="tnum"
      />
      {suffix ? <span className="text-dim shrink-0 text-[11px]">{suffix}</span> : null}
    </div>
  );

  const t = useT();
  return (
    <div className="block">
      <span className="eyebrow mb-1.5 block">
        {label}
        {required ? <span style={{ color: "var(--danger)" }}> *</span> : null}
      </span>
      <div className="flex items-center gap-2">
        {box("min")}
        <span className="text-dim shrink-0 text-[11px]">{t("common.to").toLowerCase()}</span>
        {box("max")}
      </div>
      <FieldNote hint={hint} error={error} />
    </div>
  );
}

/**
 * A from/to pair of clock times, `HH:mm` — "between 13:00 and 23:00".
 *
 * Minutes, not whole hours: "from half past one" is a real answer, and an hour
 * box with a ":00" painted after it could not give it. An end that is not after
 * the start is a legitimate window over midnight, so it is not treated as an
 * error here — the caller says what it means in the hint.
 */
export function TimeRangeField({
  label,
  hint,
  error,
  required,
  disabled,
  value,
  onChange,
}: Common & ValueProps<{ start: string; end: string }>) {
  const t = useT();
  const box = (which: "start" | "end") => (
    <Input
      type="time"
      step={60}
      value={value[which]}
      disabled={disabled}
      invalid={Boolean(error)}
      onChange={(event) => onChange({ ...value, [which]: event.target.value })}
      className="tnum min-w-0 flex-1"
    />
  );

  return (
    <div className="block">
      <span className="eyebrow mb-1.5 block">
        {label}
        {required ? <span style={{ color: "var(--danger)" }}> *</span> : null}
      </span>
      <div className="flex items-center gap-2">
        {box("start")}
        <span className="text-dim shrink-0 text-[11px]">{t("common.to").toLowerCase()}</span>
        {box("end")}
      </div>
      <FieldNote hint={hint} error={error} />
    </div>
  );
}

/**
 * A fixed-length row of single-number boxes — the draw result entry.
 *
 * Each box is its own input so the caret never has to be placed inside a long
 * string, and typing a digit advances to the next one the way a PIN field does.
 */
export function NumberSlots({
  values,
  onChange,
  min,
  max,
  labelPrefix = "P",
  ariaLabel,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  min: number;
  max: number;
  labelPrefix?: string;
  ariaLabel: string;
}) {
  const groupId = useId();
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  function set(index: number, raw: string) {
    const digits = raw.replace(/[^0-9]/g, "").slice(0, 2);
    const next = [...values];
    next[index] = digits;
    onChange(next);
    // Two digits is as long as any number here gets, so move on.
    if (digits.length === 2) refs.current[index + 1]?.focus();
  }

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={ariaLabel}>
      {values.map((value, index) => {
        const number = Number(value);
        const invalid = value !== "" && (number < min || number > max);
        return (
          <div
            key={`${groupId}-${index}`}
            className="flex flex-col items-center gap-1"
          >
            <span className="eyebrow">
              {labelPrefix}
              {index + 1}
            </span>
            <input
              ref={(el) => {
                refs.current[index] = el;
              }}
              value={value}
              onChange={(event) => set(index, event.target.value)}
              onKeyDown={(event) => {
                // Backspace on an empty box steps back, like a PIN field.
                if (event.key === "Backspace" && value === "")
                  refs.current[index - 1]?.focus();
              }}
              inputMode="numeric"
              aria-label={`${ariaLabel} ${index + 1}`}
              aria-invalid={invalid}
              className={cx(
                CONTROL,
                FOCUS,
                "money size-12 text-center text-[16px]",
              )}
              style={{
                background: "var(--surface-inset)",
                borderColor: invalid ? "var(--danger)" : "var(--border)",
                color: "var(--accent)",
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
